<template>
    <PageHeader :title="$t('starter_selector.title')" @back="goBack" />

    <div class="flex flex-1 flex-col space-y-4 p-4 overflow-y-hidden">
        <div v-if="starterSelectorContent">
            <p class="text-lg font-extrabold text-white uppercase">
                {{ $t(starterSelectorContent.titleKey) }}
            </p>
            <p class="pt-2 text-sm text-white">
                {{ $t(starterSelectorContent.descriptionKey) }}
            </p>
        </div>
        <p v-else class="text-lg font-extrabold text-white uppercase">
            {{
                isKillerGame || isCamelraceGame
                    ? $t('starter_selector.assign_heading')
                    : $t('starter_selector.decide_heading')
            }}
        </p>

        <DiagonalCutButton
            @click="selectRandom"
            :disabled="isShuffling"
            variant="primary"
            cut-position="left-top"
            data-testid="auto-select-starter-button"
        >
            {{
                isShuffling
                    ? $t('starter_selector.determining')
                    : selectedOption
                      ? $t('starter_selector.re_select')
                      : $t('starter_selector.auto_select')
            }}
        </DiagonalCutButton>

        <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div class="h-0.5 w-full bg-zinc-700"></div>
            <div class="text-red-500">{{ $t('starter_selector.or') }}</div>
            <div class="h-0.5 w-full bg-zinc-700"></div>
        </div>

        <div
            v-if="isKillerGame || (isCamelraceGame && !isTeamGame)"
            class=" grid grid-cols-2 gap-4 flex-1 overflow-y-auto"
        >
            <div
                v-for="player in players"
                :key="player.id"
                class="bg-bg border-stroke-1 flex flex-col gap-3 rounded border p-3"
            >
                <Avatar :src="player.avatar" />
                <p class="truncate font-bold text-white">{{ player.tag_name || player.name }}</p>
                <div>
                    <input
                        v-model.number="playerTargets[player.profile_id]"
                        type="number"
                        min="1"
                        max="20"
                        :placeholder="$t('starter_selector.number_placeholder')"
                        :class="[
                            'bg-card w-full border p-3 text-center text-white',
                            targetErrors[player.profile_id]
                                ? 'border-2 border-red-500 ring-2 ring-red-500'
                                : 'border-stroke-1 focus:border-red-500 focus:ring-2 focus:ring-red-500',
                        ]"
                        @input="validateTarget(player.profile_id, $event.target.value)"
                    />
                    <div v-if="targetErrors[player.profile_id]" class="mt-2 text-xs text-red-500">
                        {{ targetErrors[player.profile_id] }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isCamelraceGame && isTeamGame" class="mb-6 grid grid-cols-2 gap-4">
            <div
                v-for="team in teams"
                :key="team.id"
                class="bg-bg border-stroke-1 flex flex-col gap-3 rounded border p-3"
            >
                <Avatar :src="team.players?.[0]?.avatar" />
                <p class="truncate font-bold text-white">{{ team.name }}</p>
                <div>
                    <input
                        v-model.number="teamTargets[team.id]"
                        type="number"
                        min="1"
                        max="20"
                        placeholder="1-20"
                        :class="[
                            'bg-card w-full border p-3 text-center text-white',
                            targetErrors[team.id]
                                ? 'border-2 border-red-500 ring-2 ring-red-500'
                                : 'border-stroke-1 focus:border-red-500 focus:ring-2 focus:ring-red-500',
                        ]"
                        @input="validateTeamTarget(team.id, $event.target.value)"
                    />
                    <div v-if="targetErrors[team.id]" class="mt-2 text-xs text-red-500">
                        {{ targetErrors[team.id] }}
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="!isTeamGame && !isKillerGame && !isCamelraceGame"
            class="flex flex-1 flex-col overflow-y-auto"
        >
            <PlayerStartCard
                v-for="(player, index) in players"
                :key="player.id"
                :player="player"
                :is-selected="selectedOption === player.team"
                :is-shuffling="isShuffling && selectedOption === player.team"
                :has-selection="!!selectedOption?.id"
                :index="index"
                @select="selectPlayer"
            />
        </div>

        <div
            v-if="isTeamGame && !isKillerGame && !isCamelraceGame"
            class="grid max-h-100 flex-1 grid-flow-col grid-cols-2 grid-rows-2 space-y-4"
        >
            <div v-for="team in teams" :key="team.id" class="contents">
                <PlayerStartCard
                    v-for="player in team.players"
                    :key="player.id"
                    :player="player"
                    :is-selected="selectedOption === team.id"
                    :is-shuffling="isShuffling && selectedOption === team.id"
                    :has-selection="!!selectedOption?.id"
                    @select="selectTeam(team)"
                />
            </div>
        </div>
    </div>

    <div class="mt-auto mb-4 px-4">
        <div class=" flex flex-col space-y-3">
            <DiagonalCutButton @click="goBack" variant="secondary" cut-position="right-top">
                <svg class="size-3.5" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M3.41436 7.00008L8.70726 1.70718L7.29304 0.292969L0.585938 7.00008L7.29304 13.7072L8.70726 12.293L3.41436 7.00008Z"
                        fill="currentColor"
                    />
                </svg>
                <span class="mt-0.5">{{ $t('general.previous') }}</span>
            </DiagonalCutButton>
            <DiagonalCutButton
                @click="startGame"
                :disabled="
                    isStarting ||
                    isShuffling ||
                    (isKillerGame || isCamelraceGame ? !areAllTargetsAssigned() : !selectedOption)
                "
                variant="primary"
                cut-position="left-top"
                data-testid="start-game-button"
            >
                <span v-if="isStarting" class="flex items-center gap-2">
                    <svg
                        class="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    <span class="mt-0.5">{{ $t('starter_selector.starting') }}</span>
                </span>
                <span v-else class="flex items-center gap-2">
                    <span class="mt-0.5">{{ $t('starter_selector.start_game') }}</span>
                    <svg class="size-3.5" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                            fill="currentColor"
                        />
                    </svg>
                </span>
            </DiagonalCutButton>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import PageHeader from '@/components/PageHeader.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import PlayerStartCard from '@/components/players/PlayerStartCard.vue'
import Avatar from '@/components/Avatar.vue'
import x01Game from '@/services/games/1_x01'
import cricketGame from '@/services/games/2_cricket'
import camelraceGame from '@/services/games/3_camelrace'
import killerGame from '@/services/games/4_killer'

export default {
    name: 'StarterSelectorView',
    components: {
        Avatar,
        DiagonalCutButton,
        PlayerStartCard,
        PageHeader,
    },
    data() {
        return {
            selectedOption: null,
            // How the starting player was picked, for the starter_selected event: 'manual' when
            // tapped, 'random' via the shuffle, 'target' when the game derives it from the
            // assigned targets (Killer, Camelrace).
            starterMethod: null,
            playerTargets: {},
            teamTargets: {},
            targetErrors: {},
            validationTimeout: null,
            isStarting: false,
            isShuffling: false,
            lastRandomIndex: -1,
        }
    },
    computed: {
        ...mapStores(useMatchStore),

        stepInfo() {
            return this.matchStore.getStepInfo()
        },
        isTeamGame() {
            return this.matchStore.isTeamPlay
        },
        isKillerGame() {
            return this.matchStore.setup.matchtype_id === 4
        },
        isCamelraceGame() {
            return this.matchStore.setup.matchtype_id === 7
        },
        teams() {
            return this.matchStore.getTeams
        },
        players() {
            return this.matchStore.getProfiles
        },
        starterSelectorContent() {
            const gameServices = {
                1: x01Game,
                3: cricketGame,
                4: killerGame,
                7: camelraceGame,
            }
            return gameServices[this.matchStore.setup.matchtype_id]?.content?.starterSelectorContent
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId
        if (
            matchId &&
            this.matchStore.setup.id !== matchId &&
            this.matchStore.match.id !== matchId
        ) {
            try {
                await this.matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('Failed to restore match:', error)
                this.$router.replace({ name: 'games' })
                return
            }
        }

        this.matchStore.setCurrentStep(4)
    },
    methods: {
        goBack() {
            const matchId = this.$route.params.matchId
            if (matchId) {
                this.$router.push({
                    name: 'match-lobby',
                    params: { matchId },
                })
            } else {
                this.$router.push({ name: 'home' })
            }
        },
        selectPlayer(player) {
            if (this.isShuffling) return
            this.selectedOption = player.team
            this.starterMethod = 'manual'
        },

        selectTeam(team) {
            if (this.isShuffling) return
            this.selectedOption = team.id
            this.starterMethod = 'manual'
        },

        selectRandom() {
            if (this.isCamelraceGame && this.isTeamGame) {
                const availableTargets = Array.from({ length: 20 }, (_, index) => index + 1)
                const assignments = {}

                this.teams.forEach((team) => {
                    const randomIndex = Math.floor(Math.random() * availableTargets.length)
                    const target = availableTargets.splice(randomIndex, 1)[0]
                    assignments[team.id] = target
                })

                this.teamTargets = assignments
                this.targetErrors = {}
            } else if (this.isKillerGame || this.isCamelraceGame) {
                const availableTargets = Array.from({ length: 20 }, (_, index) => index + 1)
                const assignments = {}

                if (this.players.length > availableTargets.length) {
                    console.warn('Not enough unique targets available for all players.')
                    return
                }

                this.players.forEach((player) => {
                    const randomIndex = Math.floor(Math.random() * availableTargets.length)
                    const target = availableTargets.splice(randomIndex, 1)[0]
                    assignments[player.profile_id] = target
                })

                this.playerTargets = assignments
                this.targetErrors = {}
            } else {
                if (this.isShuffling) return

                this.isShuffling = true
                this.selectedOption = null

                const availableIndices = Array.from(
                    { length: this.players.length },
                    (_, i) => i,
                ).filter((i) => i !== this.lastRandomIndex)

                const finalIndex =
                    availableIndices[Math.floor(Math.random() * availableIndices.length)]

                setTimeout(() => {
                    this.selectedOption = this.players[finalIndex].team
                    this.lastRandomIndex = finalIndex
                    this.isShuffling = false
                    this.starterMethod = 'random'
                }, 1200)
            }
        },
        validateTarget(playerId, value) {
            // Clear existing timeout
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout)
            }

            // Clear error immediately when typing
            this.targetErrors = { ...this.targetErrors }
            delete this.targetErrors[playerId]

            // Debounce validation by 500ms
            this.validationTimeout = setTimeout(() => {
                const targetNumber = parseInt(value)

                if (isNaN(targetNumber) || value === '') {
                    return
                }

                if (targetNumber < 1 || targetNumber > 20) {
                    this.targetErrors[playerId] = this.$t('starter_selector.target_range_error')
                    return
                }

                const otherPlayerWithSameTarget = Object.entries(this.playerTargets).find(
                    ([otherId, target]) =>
                        parseInt(otherId) !== parseInt(playerId) && target === targetNumber,
                )

                if (otherPlayerWithSameTarget) {
                    const [otherPlayerId] = otherPlayerWithSameTarget
                    const otherPlayer = this.players.find(
                        (p) => p.profile_id === parseInt(otherPlayerId),
                    )
                    const otherPlayerName =
                        otherPlayer?.tag_name || otherPlayer?.name || this.$t('starter_selector.another_player')

                    this.targetErrors[playerId] =
                        this.$t('starter_selector.target_duplicate_error', { name: otherPlayerName, target: targetNumber })
                }
            }, 500)
        },
        validateTeamTarget(teamId, value) {
            // Clear existing timeout
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout)
            }

            // Clear error immediately when typing
            this.targetErrors = { ...this.targetErrors }
            delete this.targetErrors[teamId]

            // Debounce validation by 500ms
            this.validationTimeout = setTimeout(() => {
                const targetNumber = parseInt(value)

                if (isNaN(targetNumber) || value === '') {
                    return
                }

                if (targetNumber < 1 || targetNumber > 20) {
                    this.targetErrors[teamId] = this.$t('starter_selector.target_range_error')
                    return
                }

                const otherTeamWithSameTarget = Object.entries(this.teamTargets).find(
                    ([otherId, target]) =>
                        String(otherId) !== String(teamId) && target === targetNumber,
                )

                if (otherTeamWithSameTarget) {
                    const [otherTeamId] = otherTeamWithSameTarget
                    const otherTeam = this.teams.find((t) => t.id === otherTeamId)
                    const otherTeamName = otherTeam?.name || this.$t('starter_selector.another_team')

                    this.targetErrors[teamId] = this.$t('starter_selector.target_duplicate_error', {
                        name: otherTeamName,
                        target: targetNumber,
                    })
                }
            }, 500)
        },
        areAllTargetsAssigned() {
            if (!this.isKillerGame && !this.isCamelraceGame) return true

            const hasErrors = Object.keys(this.targetErrors).length > 0
            if (hasErrors) return false

            if (this.isCamelraceGame && this.isTeamGame) {
                const validTargets = Object.values(this.teamTargets).filter(
                    (t) => t >= 1 && t <= 20,
                )
                return validTargets.length === this.teams.length
            }

            const validTargets = Object.values(this.playerTargets).filter((t) => t >= 1 && t <= 20)
            return validTargets.length === this.players.length
        },
        getKillerStartingPlayer() {
            const [profileId] = Object.entries(this.playerTargets).reduce(
                ([maxId, maxTarget], [id, target]) =>
                    target > maxTarget ? [id, target] : [maxId, maxTarget],
                [null, 0],
            )
            const player = this.players.find((p) => p.profile_id === parseInt(profileId))
            return player?.team
        },
        getCamelraceStartingPlayer() {
            // The player/team assigned the lowest segment number starts.
            if (this.isTeamGame) {
                const [teamId] = Object.entries(this.teamTargets).reduce(
                    ([minId, minTarget], [id, target]) =>
                        minTarget === null || target < minTarget ? [id, target] : [minId, minTarget],
                    [null, null],
                )
                return teamId
            }
            const [profileId] = Object.entries(this.playerTargets).reduce(
                ([minId, minTarget], [id, target]) =>
                    minTarget === null || target < minTarget ? [id, target] : [minId, minTarget],
                [null, null],
            )
            const player = this.players.find((p) => p.profile_id === parseInt(profileId))
            return player?.team
        },
        async startGame() {
            if (this.isStarting) return

            let startingPlayer = this.selectedOption

            if (this.isKillerGame || this.isCamelraceGame) {
                if (!this.areAllTargetsAssigned()) {
                    this.$toast(
                        this.$t('general.error'),
                        5000,
                        this.$t('starter_selector.target_range_error'),
                    )
                    return
                }
                if (this.isKillerGame) {
                    startingPlayer = this.getKillerStartingPlayer()
                    console.log('🎯 Killer starting player (highest target):', startingPlayer)
                } else if (this.isCamelraceGame) {
                    startingPlayer = this.getCamelraceStartingPlayer()
                    console.log('🐫 Camelrace starting player (lowest target):', startingPlayer)
                }
            } else if (!this.selectedOption) {
                return
            }

            this.isStarting = true

            this.$analytics.trackEvent(ANALYTICS_EVENTS.STARTER_SELECTED, {
                method:
                    this.isKillerGame || this.isCamelraceGame
                        ? 'target'
                        : (this.starterMethod ?? 'manual'),
                play_mode: this.isTeamGame ? 'teams' : 'singles',
            })

            try {
                this.matchStore.setup.starting_player = startingPlayer
                if (this.isKillerGame) {
                    this.matchStore.setup.player_targets = this.playerTargets
                    console.log('🎯 Killer game targets assigned:', this.playerTargets)
                }
                if (this.isCamelraceGame) {
                    // Build team_numbers: map team UUID → chosen section number.
                    // In team mode each team picks one lane; in singles each player
                    // is their own team, so the number is keyed by their team UUID.
                    const teamNumbers = {}
                    if (this.isTeamGame) {
                        this.teams.forEach((team) => {
                            const section = this.teamTargets[team.id]
                            if (section && team.id) {
                                teamNumbers[team.id] = section
                            }
                        })
                    } else {
                        this.players.forEach((player) => {
                            const section = this.playerTargets[player.profile_id]
                            if (section && player.team) {
                                teamNumbers[player.team] = section
                            }
                        })
                    }
                    this.matchStore.setup.team_numbers = teamNumbers
                    console.log('🐫 Camelrace team numbers assigned:', teamNumbers)
                }
                await this.matchStore.startMatch()
                this.$router.push({
                    name: 'match-play',
                    params: { matchId: this.$route.params.matchId },
                })
            } catch (error) {
                console.error('Failed to start game:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('general.error_retry'))
            } finally {
                this.isStarting = false
            }
        },
    },
    beforeUnmount() {
        if (this.shuffleInterval) {
            clearInterval(this.shuffleInterval)
        }
        if (this.validationTimeout) {
            clearTimeout(this.validationTimeout)
        }
    },
}
</script>
